import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert,
  Image,
  Dropdown,
  InputGroup
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';
import { productsAPI } from '../services/api';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';

// Define interface for productsAPI to include catalog methods
interface ProductsAPI {
  getProductById: (id: string) => Promise<AxiosResponse>;
  getCategories: () => Promise<AxiosResponse>;
  createProduct: (formData: FormData) => Promise<AxiosResponse>;
  updateProduct: (id: string, formData: FormData) => Promise<AxiosResponse>;
  deleteProductImage: (productId: string, imageId: string) => Promise<AxiosResponse>;
  setMainProductImage: (productId: string, imageId: string) => Promise<AxiosResponse>;
  deleteProductCatalog: (productId: string) => Promise<AxiosResponse>;
  uploadProductCatalog: (productId: string, formData: FormData) => Promise<AxiosResponse>;
  downloadProductCatalog: (productId: string) => Promise<AxiosResponse>;
}

// Cast the imported productsAPI to our interface
const typedProductsAPI = productsAPI as unknown as ProductsAPI;

// Simplified form interface to match backend
interface ProductFormValues {
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: string;
  catalogDescription: string;
}

// Simplified validation schema
const ProductSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  nameAr: Yup.string(),
  description: Yup.string(),
  descriptionAr: Yup.string(),
  category: Yup.string(),
  catalogDescription: Yup.string(),
});

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{id: string, url: string, isMain?: boolean}[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [catalogFile, setCatalogFile] = useState<File | null>(null);
  const [existingCatalog, setExistingCatalog] = useState<{path: string, description: string} | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [categoryInputFocused, setCategoryInputFocused] = useState(false);
  
  // Move initialValues to state so they can be properly updated
  const [initialValues, setInitialValues] = useState<ProductFormValues>({
    name: '',
    nameAr: '',
    description: '',
    descriptionAr: '',
    category: '',
    catalogDescription: '',
  });

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await typedProductsAPI.getCategories();
        if (Array.isArray(response.data)) {
          setCategories(response.data);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch product data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchProduct = async () => {
        try {
          const response = await typedProductsAPI.getProductById(id);
          const product = response.data;
          
          // Set form initial values properly using setState
          setInitialValues({
            name: product.name || '',
            nameAr: product.nameAr || '',
            description: product.description || '',
            descriptionAr: product.descriptionAr || '',
            category: product.category || '',
            catalogDescription: product.catalogDescription || '',
          });
          
          // Set existing images
          if (product.images && product.images.length > 0) {
            const formattedImages = product.images.map((img: any) => ({
              id: img._id || img.id,
              url: `${img.path || img}`,
              isMain: img.isMain
            }));
            setExistingImages(formattedImages);
          }
          
          // Set existing catalog
          if (product.catalogFile || (product.catalog && product.catalog.file)) {
            const catalogPath = product.catalogFile || (product.catalog && product.catalog.file);
            setExistingCatalog({
              path: `${process.env.REACT_APP_API_URL_WITHOUT_API}/${catalogPath}`,
              description: product.catalogDescription || (product.catalog && product.catalog.description) || ''
            });
          }
          
          setLoading(false);
        } catch (err: any) {
          setError('Failed to fetch product details');
          console.error(err);
          setLoading(false);
        }
      };
      
      fetchProduct();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  // Handle form submission
  const handleSubmit = async (values: ProductFormValues, { setSubmitting }: any) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('nameAr', values.nameAr);
      formData.append('description', values.description);
      formData.append('descriptionAr', values.descriptionAr);
      formData.append('category', values.category);
      
      // Append new images
      images.forEach(image => {
        formData.append('images', image);
      });
      
      // Append catalog file if exists
      if (catalogFile) {
        formData.append('catalogFile', catalogFile);
        formData.append('catalogDescription', values.catalogDescription);
      } else if (existingCatalog && values.catalogDescription) {
        // Include catalog description even if not uploading a new file
        formData.append('catalogDescription', values.catalogDescription);
      }
      
      console.log('Updating product with data:', Object.fromEntries(formData.entries()));
      
      let response;
      if (isEditMode && id) {
        response = await typedProductsAPI.updateProduct(id, formData);
        console.log('Update response:', response.data);
        setSuccess('Product updated successfully');
      } else {
        response = await typedProductsAPI.createProduct(formData);
        setSuccess('Product created successfully');
      }
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/products');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save product');
      console.error('Error updating product:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setImages(prev => [...prev, ...selectedFiles]);
      
      // Create previews for the new images
      const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  // Remove selected image
  const removeSelectedImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Delete existing image
  const deleteExistingImage = async (imageId: string) => {
    if (!id) return;
    
    try {
      await typedProductsAPI.deleteProductImage(id, imageId);
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
      setSuccess('Image deleted successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to delete image');
      console.error(err);
    }
  };

  // Set main product image
  const setMainImage = async (imageId: string) => {
    if (!id) return;
    
    try {
      await typedProductsAPI.setMainProductImage(id, imageId);
      
      // Update local state to reflect the change
      setExistingImages(prev => 
        prev.map(img => ({
          ...img,
          isMain: img.id === imageId
        }))
      );
      
      setSuccess('Main image updated successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to update main image');
      console.error(err);
    }
  };

  // Handle catalog file selection
  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCatalogFile(e.target.files[0]);
    }
  };

  // Delete existing catalog
  const deleteExistingCatalog = async () => {
    if (!id) return;
    
    try {
      await typedProductsAPI.deleteProductCatalog(id);
      setExistingCatalog(null);
      setSuccess('Catalog deleted successfully');
      
      // Clear success message after a delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError('Failed to delete catalog');
      console.error(err);
    }
  };

  // Handle catalog upload separately if needed
  const handleCatalogUpload = async (productId: string) => {
    if (!catalogFile) return;
    
    try {
      const formData = new FormData();
      formData.append('catalog', catalogFile);
      formData.append('catalogDescription', initialValues.catalogDescription);
      
      await typedProductsAPI.uploadProductCatalog(productId, formData);
      setSuccess('Catalog uploaded successfully');
    } catch (err: any) {
      setError('Failed to upload catalog');
      console.error(err);
    }
  };

  // Download catalog
  const downloadCatalog = async (productId: string) => {
    try {
      const response = await typedProductsAPI.downloadProductCatalog(productId);
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product-catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download catalog');
      console.error(err);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category: string, setFieldValue: any) => {
    setFieldValue('category', category);
    setShowCategoryDropdown(false);
  };

  // Handle custom category input
  const handleCustomCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomCategory(e.target.value);
  };

  // Add custom category to field
  const handleAddCustomCategory = (setFieldValue: any) => {
    if (customCategory.trim()) {
      setFieldValue('category', customCategory.trim());
      setShowCategoryDropdown(false);
      setCustomCategory('');
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading product data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
        </Col>
        <Col xs="auto">
          <Link to="/products">
            <Button variant="outline-secondary">
              <Icon icon={Icons.FaArrowLeft} className="me-2" /> Back to Products
            </Button>
          </Link>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={ProductSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={8}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Name (English)</Form.Label>
                          <Form.Control
                        type="text"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Name (Arabic)</Form.Label>
                          <Form.Control
                            type="text"
                            name="nameAr"
                            value={values.nameAr}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.nameAr && !!errors.nameAr}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.nameAr}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Description (English)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.description && !!errors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Description (Arabic)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="descriptionAr"
                        value={values.descriptionAr}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.descriptionAr && !!errors.descriptionAr}
                        dir="rtl"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.descriptionAr}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          name="category"
                          value={values.category}
                          onChange={handleChange}
                          onBlur={(e) => {
                            handleBlur(e);
                            setTimeout(() => setCategoryInputFocused(false), 200);
                          }}
                          onFocus={() => setCategoryInputFocused(true)}
                          isInvalid={touched.category && !!errors.category}
                          onClick={() => setShowCategoryDropdown(true)}
                          placeholder="Select or enter a category"
                        />
                        <Button 
                          variant="outline-secondary"
                          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                        >
                          <Icon icon={Icons.FaChevronDown} />
                        </Button>
                        <Form.Control.Feedback type="invalid">
                          {errors.category}
                        </Form.Control.Feedback>
                      </InputGroup>
                      {(showCategoryDropdown || categoryInputFocused) && (
                        <div className="position-relative">
                          <div 
                            className="position-absolute mt-1 w-100 bg-white border rounded shadow-sm" 
                            style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}
                          >
                            {categories.length > 0 ? (
                              <>
                                {categories.map((category, index) => (
                                  <div 
                                    key={index} 
                                    className="p-2 border-bottom cursor-pointer hover:bg-light"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleCategorySelect(category, setFieldValue)}
                                  >
                                    {category}
                                  </div>
                                ))}
                                <div className="p-2 border-top">
                                  <Form.Control
                                    type="text"
                                    placeholder="Add custom category"
                                    value={customCategory}
                                    onChange={handleCustomCategoryChange}
                                    onClick={(e) => e.stopPropagation()}
                                    className="mb-2"
                                  />
                                  <Button 
                                    variant="primary" 
                                    size="sm" 
                                    onClick={() => handleAddCustomCategory(setFieldValue)}
                                    className="w-100"
                                  >
                                    <Icon icon={Icons.FaPlus} className="me-1" /> Add Custom Category
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="p-3 text-center text-muted">
                                <p>No categories found. Enter a custom category.</p>
                                <Form.Control
                                  type="text"
                                  placeholder="Add custom category"
                                  value={customCategory}
                                  onChange={handleCustomCategoryChange}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mb-2"
                                />
                                <Button 
                                  variant="primary" 
                                  size="sm" 
                                  onClick={() => handleAddCustomCategory(setFieldValue)}
                                  className="w-100"
                                >
                                  <Icon icon={Icons.FaPlus} className="me-1" /> Add Custom Category
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Select from existing categories or add a new one
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col lg={4}>
                    <Card className="bg-light">
                      <Card.Body>
                        <h5 className="mb-3">Product Images</h5>
                        
                        {/* Existing Images */}
                        {existingImages.length > 0 && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Current Images</p>
                            <Row xs={2} className="g-2 mb-3">
                              {existingImages.map((img) => (
                                <Col key={img.id}>
                                  <div className="position-relative">
                                    <Image 
                                      src={img.url} 
                                      alt="Product" 
                                      className="img-thumbnail" 
                                      style={{ height: '100px', objectFit: 'cover' }}
                                    />
                                    <div className="position-absolute top-0 end-0 d-flex">
                                      {img.isMain ? (
                                        <span className="badge bg-primary">
                                          <Icon icon={Icons.FaStar} /> Main
                                        </span>
                                      ) : (
                                        <Button 
                                          variant="light" 
                                          size="sm" 
                                          className="p-1"
                                          onClick={() => setMainImage(img.id)}
                                          title="Set as main image"
                                        >
                                          <Icon icon={Icons.FaStar} />
                                        </Button>
                                      )}
                                      <Button 
                                        variant="danger" 
                                        size="sm" 
                                        className="p-1 ms-1"
                                        onClick={() => deleteExistingImage(img.id)}
                                        title="Delete image"
                                      >
                                        <Icon icon={Icons.FaTrash} />
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                        
                        {/* New Images */}
                        <div className="mb-3">
                          <p className="text-muted mb-2">Add New Images</p>
                          <Form.Control 
                            type="file" 
                            onChange={handleImageChange} 
                            accept="image/*"
                            multiple
                          />
                          <Form.Text className="text-muted">
                            Select up to 5 images. Maximum file size: 5MB.
                          </Form.Text>
                        </div>
                        
                        {/* New Image Previews */}
                        {imagePreviews.length > 0 && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">New Images Preview</p>
                            <Row xs={2} className="g-2">
                              {imagePreviews.map((preview, index) => (
                                <Col key={index}>
                                  <div className="position-relative">
                                    <Image 
                                      src={preview} 
                                      alt={`Preview ${index + 1}`} 
                                      className="img-thumbnail" 
                                      style={{ height: '100px', objectFit: 'cover' }}
                                    />
                                    <Button 
                                      variant="danger" 
                                      size="sm" 
                                      className="position-absolute top-0 end-0 p-1"
                                      onClick={() => removeSelectedImage(index)}
                                      title="Remove image"
                                    >
                                      <Icon icon={Icons.FaTrash} />
                                    </Button>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        )}
                        
                        {/* No Images Info */}
                        {existingImages.length === 0 && imagePreviews.length === 0 && (
                          <div className="text-center p-4 bg-white rounded mb-3">
                            <Icon icon={Icons.FaImage} className="display-4 text-muted mb-3" />
                            <p>No images selected</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                    
                    {/* Catalog File Section */}
                    <Card className="bg-light mt-3">
                      <Card.Body>
                        <h5 className="mb-3">Product Catalog (PDF)</h5>
                        
                        {/* Existing Catalog */}
                        {existingCatalog && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Current Catalog</p>
                            <div className="d-flex align-items-center bg-white p-2 rounded">
                              <Icon icon={Icons.FaFilePdf} className="text-danger me-2" style={{ fontSize: '24px' }} />
                              <div className="flex-grow-1">
                                <div>Product Catalog</div>
                                <small className="text-muted">
                                  {existingCatalog.description || 'PDF Document'}
                                </small>
                              </div>
                              <a href={existingCatalog.path} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary me-2">
                                <Icon icon={Icons.FaEye} /> View
                              </a>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => id && downloadCatalog(id)}
                                title="Download catalog"
                              >
                                <Icon icon={Icons.FaDownload} />
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="p-1"
                                onClick={deleteExistingCatalog}
                                title="Delete catalog"
                              >
                                <Icon icon={Icons.FaTrash} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* New Catalog Upload */}
                        <div className="mb-3">
                          <p className="text-muted mb-2">{existingCatalog ? 'Replace Catalog' : 'Add Catalog'}</p>
                          <Form.Control 
                            type="file" 
                            onChange={handleCatalogChange} 
                            accept=".pdf"
                          />
                          <Form.Text className="text-muted">
                            Upload a PDF catalog for this product. Maximum file size: 5MB.
                          </Form.Text>
                        </div>
                        
                        {/* Catalog File Preview */}
                        {catalogFile && (
                          <div className="mb-3">
                            <p className="text-muted mb-2">Selected Catalog</p>
                            <div className="d-flex align-items-center bg-white p-2 rounded">
                              <Icon icon={Icons.FaFilePdf} className="text-danger me-2" style={{ fontSize: '24px' }} />
                              <div className="flex-grow-1">
                                <div>{catalogFile.name}</div>
                                <small className="text-muted">{(catalogFile.size / 1024).toFixed(2)} KB</small>
                              </div>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                className="p-1"
                                onClick={() => setCatalogFile(null)}
                                title="Remove catalog"
                              >
                                <Icon icon={Icons.FaTrash} />
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* No Catalog Info */}
                        {!existingCatalog && !catalogFile && (
                          <div className="text-center p-4 bg-white rounded mb-3">
                            <Icon icon={Icons.FaFilePdf} className="display-4 text-muted mb-3" />
                            <p>No catalog selected</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                {/* Catalog Description */}
                {/* {(existingCatalog || catalogFile) && (
                  <Form.Group className="mb-3">
                    <Form.Label>Catalog Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="catalogDescription"
                      value={values.catalogDescription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.catalogDescription && !!errors.catalogDescription}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.catalogDescription}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Provide a brief description of what's in the catalog
                    </Form.Text>
                  </Form.Group>
                )} */}
                
                <div className="d-flex justify-content-end mt-4">
                  <Button 
                    variant="secondary" 
                    as={Link as any} 
                    to="/products" 
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={isSubmitting}
                  >
                    <Icon icon={Icons.FaSave} className="me-2" />
                    {isSubmitting ? 'Saving...' : 'Save Product'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProductForm; 